import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Minus, Plus, Trash2, Search, PencilLine, Clock, X } from 'lucide-react';
import { money, unitLocal, orderTotals, fmtDate } from '../../lib/orderMoney';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLE = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700', withdrawn: 'bg-neutral-200 text-neutral-600', superseded: 'bg-neutral-200 text-neutral-600' };

export const EditStatusPill = ({ status }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ${STATUS_STYLE[status] || STATUS_STYLE.withdrawn}`}>{status}</span>
);

const AddProduct = ({ onAdd }) => {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined; }
    const h = setTimeout(() => axios.get(`${API}/products/suggest`, { params: { q } }).then(({ data }) => setRes(data || [])).catch(() => {}), 250);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-[#0f0f10] px-3">
        <Search size={14} className="text-neutral-400" />
        <input data-testid="edit-add-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a product — search by name"
          className="flex-1 bg-transparent py-2 text-[13px] outline-none" />
      </div>
      {res.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] shadow-xl max-h-60 overflow-y-auto">
          {res.map(p => (
            <button type="button" key={p.id} data-testid={`edit-add-result-${p.id}`} onClick={() => { onAdd(p); setQ(''); }}
              className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-neutral-50 dark:hover:bg-neutral-800 flex justify-between gap-2">
              <span className="truncate">{p.name} <span className="text-neutral-500">{p.strength}</span></span><span className="text-neutral-500 shrink-0">${Number(p.price).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const QtyRow = ({ order, it, onQty }) => (
  <div data-testid={`edit-item-${it.id}`} className="flex items-center gap-3 py-2">
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100 truncate">{it.name}</div>
      <div className="text-[11.5px] text-neutral-500">{[it.strength, it.packaging].filter(Boolean).join(' · ')} · {money(order, unitLocal(order, it.price))} each</div>
    </div>
    <div className="flex items-center rounded-lg border border-neutral-200 dark:border-neutral-700">
      <button type="button" data-testid={`edit-dec-${it.id}`} onClick={() => onQty(it.qty - 1)} className="p-1.5"><Minus size={13} /></button>
      <span data-testid={`edit-qty-${it.id}`} className="w-8 text-center text-[13px]">{it.qty}</span>
      <button type="button" data-testid={`edit-inc-${it.id}`} onClick={() => onQty(it.qty + 1)} className="p-1.5"><Plus size={13} /></button>
    </div>
    <button type="button" data-testid={`edit-remove-${it.id}`} onClick={() => onQty(0)} className="p-1.5 text-neutral-400 hover:text-red-500"><Trash2 size={14} /></button>
  </div>
);

export const EditOrderCard = ({ order, requests, canEdit, token, headers, onDone }) => {
  const pending = requests.find(r => r.status === 'pending');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const start = () => { setDraft(((pending || order).items || []).map(i => ({ ...i, id: String(i.id) }))); setNote(''); setErr(''); setEditing(true); };
  const setQty = (id, qty) => setDraft(d => (qty <= 0 ? d.filter(i => i.id !== id) : d.map(i => (i.id === id ? { ...i, qty: Math.min(qty, 500) } : i))));
  const add = (p) => setDraft(d => (d.some(i => i.id === String(p.id)) ? d.map(i => (i.id === String(p.id) ? { ...i, qty: i.qty + 1 } : i))
    : [...d, { id: String(p.id), name: p.name, strength: p.strength || '', packaging: p.packaging || '', catLabel: p.catLabel || '', image: p.image || '', price: p.price, qty: 1 }]));
  const estimate = useMemo(() => draft.reduce((a, i) => a + unitLocal(order, i.price) * i.qty, 0), [draft, order]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/order-portal/${order.id}/edit-request`, { t: token, note, items: draft.map(({ price, price_local, ...rest }) => rest) }, { headers });
      setEditing(false); onDone();
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Could not send your request'); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    await axios.delete(`${API}/order-portal/${order.id}/edit-request`, { params: { t: token }, headers }).catch(() => {});
    onDone();
  };

  return (
    <div data-testid="edit-order-card" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <PencilLine size={15} className="text-brand" /><h3 className="text-[13.5px] font-semibold text-neutral-900 dark:text-neutral-100">Change your order</h3>
        {canEdit && !editing && <button data-testid="edit-order-start" onClick={start} className="ml-auto text-[12.5px] font-semibold text-brand">{pending ? 'Edit request' : 'Request changes'}</button>}
      </div>
      {!canEdit && <p className="text-[12.5px] text-neutral-500">This order has shipped or closed, so it can no longer be changed. Message us below if you need help.</p>}
      {pending && !editing && (
        <div data-testid="edit-pending-box" className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-3 text-[12.5px] space-y-1.5">
          <div className="flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-300"><Clock size={13} /> Waiting for approval · sent {fmtDate(pending.created_at)}</div>
          {pending.items.map(i => <div key={i.id} className="text-neutral-700 dark:text-neutral-300">{i.qty} × {i.name}</div>)}
          <div className="font-semibold">New total: {money(order, orderTotals(order, pending).total)}</div>
          <button data-testid="edit-withdraw-btn" onClick={withdraw} className="inline-flex items-center gap-1 text-[12px] text-neutral-600 dark:text-neutral-400 hover:text-red-600"><X size={12} /> Withdraw request</button>
        </div>
      )}
      {editing && (
        <div className="space-y-3">
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">{draft.map(it => <QtyRow key={it.id} order={order} it={it} onQty={(q) => setQty(it.id, q)} />)}</div>
          <AddProduct onAdd={add} />
          <textarea data-testid="edit-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} placeholder="Note for us (optional)"
            className="w-full bg-neutral-50 dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-brand" />
          <div className="flex flex-wrap items-center gap-3">
            <span data-testid="edit-estimate" className="text-[12.5px] text-neutral-600 dark:text-neutral-300">New items subtotal ≈ <strong>{money(order, estimate)}</strong> (shipping unchanged)</span>
            <div className="ml-auto flex gap-2">
              <button onClick={() => setEditing(false)} className="rounded-lg border border-neutral-200 dark:border-neutral-700 px-3 py-2 text-[12.5px]">Cancel</button>
              <button data-testid="edit-submit-btn" disabled={busy || !draft.length} onClick={submit} className="btn-brand rounded-lg px-4 py-2 text-[12.5px] font-semibold disabled:opacity-50">{busy ? 'Sending…' : 'Send for approval'}</button>
            </div>
          </div>
          {err && <div data-testid="edit-error" className="text-[12px] text-red-600">{err}</div>}
        </div>
      )}
      {requests.filter(r => r.status !== 'pending').slice(0, 3).map(r => (
        <div key={r.id} className="text-[11.5px] text-neutral-500 flex items-center gap-2"><EditStatusPill status={r.status} /> request from {fmtDate(r.created_at)}{r.admin_note ? ` — “${r.admin_note}”` : ''}</div>
      ))}
    </div>
  );
};
